import { initContract } from '@ts-rest/core';
import { z } from 'zod';
const c = initContract();
export const AddLineToCheckoutBodySchema = z.object({
    organizationId: z.string().min(1),
    siteId: z.string().min(1),
    identityId: z.string().optional(),
    firstName: z.string().optional().default(''),
    lastName: z.string().optional().default(''),
    simId: z.string().optional(),
    simType: z.enum(['physique', 'esim']),
    physiqueOption: z.enum(['order', 'stock']),
    planId: z.string().min(1),
    orderNature: z.enum(['creation', 'portability']),
    operatorId: z.string().min(1),
    mobileLineType: z.enum(['classic', 'dataonly']),
    portedNumber: z.string().optional().default(''),
    rioNumber: z.string().optional().default(''),
    portabilityDate: z
        .union([z.date(), z.string().datetime()])
        .optional()
        .transform((v) => (typeof v === 'string' ? new Date(v) : v)),
});
export const AddSimsToCheckoutBodySchema = z.object({
    organizationId: z.string().min(1),
    operatorId: z.string().min(1),
    operatorLabel: z.string().min(1),
    quantity: z.number().int().min(5, 'La quantité minimum est de 5'),
    siteId: z.string().min(1),
});
export const RemoveItemFromCheckoutBodySchema = z.object({
    itemId: z.string(),
});
export const ConfirmCheckoutBodySchema = z.object({
    checkoutId: z.string().min(1),
    deliveryAddress: z
        .object({
        country: z.string(),
        street: z.string(),
        city: z.string(),
        zipCode: z.string(),
        isPersonal: z.boolean(),
        companyName: z.string().optional(),
        recipientName: z.string().optional(),
    })
        .nullable(),
    shippingMethod: z.enum(['LETTRE_SIMPLE', 'TNT']).optional(),
});
/**
 * Display-side metadata snapshotted on the item at write time. Shape depends
 * on the item type (DEVICE / ACCESSORY / SIM / LINE) — modeled as one object
 * of null-tolerant fields (`nullish`: the rows store explicit nulls — a
 * child SIM carries `simNumber: null`, and a plain `.optional()` made
 * `validateResponse` reject the whole cart). Consumers read with
 * `metadata?.foo`; the typed-metadata rewrite will tighten this schema.
 */
export const CheckoutItemMetadataSchema = z
    .object({
    type: z.string().nullish(),
    // DEVICE
    brand: z.string().nullish(),
    model: z.string().nullish(),
    category: z.string().nullish(),
    condition: z.string().nullish(),
    deviceCapacity: z.number().nullish(),
    deviceColor: z.string().nullish(),
    // ACCESSORY
    color: z.string().nullish(),
    compatibleDeviceBrand: z.string().nullish(),
    compatibleDeviceModel: z.string().nullish(),
    name: z.string().nullish(),
    // SIM
    siteName: z.string().nullish(),
    physiqueOption: z.enum(['order', 'stock']).nullish(),
    simNumber: z.string().nullish(),
    // TRAVEL_ESIM
    country: z.string().nullish(),
    planLabel: z.string().nullish(),
    dataLimitGb: z.number().nullish(),
    durationDays: z.number().nullish(),
    departureDate: z.string().nullish(),
    orderKind: z.enum(['CREATION', 'RECHARGE']).nullish(),
    travelEsimId: z.string().nullish(),
    returnDate: z.string().nullish(),
    beneficiary: z
        .object({
        kind: z.enum(['LINE', 'EXTERNAL']),
        name: z.string(),
        email: z.string(),
        lineNumber: z.string().nullish(),
    })
        .nullish(),
})
    .passthrough();
export const CheckoutItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    quantity: z.number(),
    /** Cents, like every money field of the API. */
    price: z.number(),
    /** Cents. */
    total: z.number(),
    isDeletable: z.boolean(),
    type: z.string(),
    isRecurring: z.boolean(),
    isFixed: z.boolean(),
    metadata: CheckoutItemMetadataSchema.nullable().optional(),
});
export const ShippingOptionSchema = z.object({
    method: z.enum(['LETTRE_SIMPLE', 'TNT']),
    /** Cents. */
    cost: z.number(),
    label: z.string(),
    description: z.string(),
});
export const CheckoutSchema = z.object({
    id: z.string(),
    /** Cents. */
    total: z.number(),
    /** Cents. */
    fixedTotal: z.number(),
    /** Cents. */
    recurringTotal: z.number(),
    totalWeight: z.number(),
    items: z.array(CheckoutItemSchema),
    availableShippingMethods: z.array(ShippingOptionSchema),
});
export const checkoutContract = c.router({
    getCheckout: {
        method: 'GET',
        path: '/checkout',
        query: z.object({ organizationId: z.string().min(1) }),
        responses: {
            /** Null when the user has no open checkout for this organization. */
            200: CheckoutSchema.nullable(),
        },
        summary: 'Get the current checkout of the user for an organization',
    },
    getCheckoutItemCount: {
        method: 'GET',
        path: '/checkout/count',
        query: z.object({ organizationId: z.string().min(1) }),
        responses: {
            200: z.object({ count: z.number() }),
        },
        summary: 'Count the items of the current checkout',
    },
    addLineToCheckout: {
        method: 'POST',
        path: '/checkout/add-line',
        body: AddLineToCheckoutBodySchema,
        responses: {
            201: z.object({ success: z.boolean() }),
        },
        summary: 'Add a mobile line to the checkout',
    },
    addSimsToCheckout: {
        method: 'POST',
        path: '/checkout/add-sims',
        body: AddSimsToCheckoutBodySchema,
        responses: {
            201: z.object({ success: z.boolean() }),
        },
        summary: 'Add a batch of SIM cards to the checkout',
    },
    removeItemFromCheckout: {
        method: 'POST',
        path: '/checkout/remove-item',
        body: RemoveItemFromCheckoutBodySchema,
        responses: {
            201: z.object({ success: z.boolean() }),
        },
        summary: 'Remove an item from the checkout',
    },
    confirmCheckout: {
        method: 'POST',
        path: '/checkout/confirm',
        body: ConfirmCheckoutBodySchema,
        responses: {
            201: z.object({ orderId: z.string(), orderNumber: z.string() }),
        },
        summary: 'Confirm the checkout and create the order',
    },
});
